import React, {useEffect} from 'react';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ConsultationPopup from '../common/ConsultationPopup';
import { motion } from 'framer-motion';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Container,
    Paper,
    Fab,
    useScrollTrigger,
    Zoom
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import StarIcon from '@mui/icons-material/Star';
import {
    heroChildrenOutdoor01,
    heroExcitedChildren,
} from '../../assets/images';
import useParallaxEffect from "../common/useParallaxEffect";


// ScrollTop component
function ScrollTop(props) {
    const { children } = props;
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 100,
    });

    const handleClick = (event) => {
        const anchor = (event.target.ownerDocument || document).querySelector(
            '#back-to-top-anchor',
        );

        if (anchor) {
            anchor.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    };

    return (
        <Zoom in={trigger}>
            <Box
                onClick={handleClick}
                role="presentation"
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    left: 16,
                    zIndex: 9998
                }}
            >
                {children}
            </Box>
        </Zoom>
    );
}
const Home = (props) => {
    const heroRef = useParallaxEffect();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <div id="back-to-top-anchor"/>
            {/* Hero Section - Fixed centering and visibility issues */}
            <Box ref={heroRef} sx={{
                    position: 'relative',
                    color: 'white',
                    minHeight: '100vh',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    paddingLeft: '0 !important',
                    paddingRight: '0 !important',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `url(${heroChildrenOutdoor01})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundAttachment: 'fixed',
                        backgroundBlendMode: 'multiply',
                        opacity: 0.7,
                        transition: 'opacity 0.5s ease-in-out',
                        zIndex: -2,
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0, 70, 70, 0.5) 0%, rgba(26, 54, 93, 0.5) 100%)',
                        zIndex: -1,
                    }
                }}>
                <Container maxWidth="lg" disableGutters sx={{
                        textAlign: 'center',
                        position: 'relative',
                        paddingLeft: '0 !important',
                        paddingRight: '0 !important',
                    }}>
                    <motion.div
                        initial={{opacity: 0, y: 50}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.8}}
                    >
                        <Box sx={{textAlign: {xs: 'center', sm: 'left'}, maxWidth: '800px', mx: 'auto', px: {xs: 2, sm: 0}}}>
                            <Typography
                                variant="h2"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: {xs: '3rem', sm: '3rem', md: '3.5rem', lg: '4rem'},
                                    lineHeight: {xs: 1, sm: 1.2},
                                    color: 'white',
                                    textShadow: '2px 2px 8px rgba(0,0,0,0.4)',
                                    mb: {xs: 2, sm: 3},
                                }}
                            >
                                ABA Therapy
                            </Typography>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: {xs: '1.5rem', sm: '2.2rem', md: '1.9rem', lg: '1.9rem'},
                                    lineHeight: 1.2,
                                    color: 'white',
                                    textShadow: '2px 2px 8px rgba(0,0,0,0.4)',
                                    mb: {xs: 4, sm: 3},
                                }}
                            >
                                Achieving success one decision & one family at a time.
                            </Typography>
                        </Box>
                    </motion.div>
                    <motion.div
                        initial={{opacity: 0, scale: 0.9}}
                        animate={{opacity: 1, scale: 1}}
                        transition={{delay: 0.6, duration: 0.5}}
                        whileHover={{scale: 1.05}}
                        whileTap={{scale: 0.95}}
                    >
                        <Button
                            variant="contained"
                            size="large"
                            sx={{
                                mt: 2,
                                bgcolor: '#C09C2C',
                                color: 'white',
                                px: {xs: 3, md: 5},
                                py: 1.8,
                                borderRadius: '30px',
                                fontSize: {xs: '1rem', md: '1.1rem'},
                                fontWeight: 600,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                                '&:hover': {
                                    bgcolor: '#C7AE6A',
                                    boxShadow: '0 6px 25px rgba(0,0,0,0.6)',
                                }
                            }}
                            component={RouterLink}
                            to="/contact"
                        >
                            GET STARTED
                        </Button>
                    </motion.div>
                </Container>
            </Box>

            {/* Vision & Mission Section */}
            <Box sx={{
                    py: 10,
                    bgcolor: '#f8f9fa',
                    position: 'relative',
                    minHeight: '600px',
                    display: 'flex',
                    alignItems: 'center',
                }}>
                <Container maxWidth="lg">
                    <Grid container spacing={5} alignItems="center" justifyContent="center">
                        <Grid item xs={12} md={10}>
                            <motion.div
                                initial={{opacity: 0, y: 30}}
                                whileInView={{opacity: 1, y: 0}}
                                viewport={{once: true}}
                                transition={{duration: 0.6}}
                            >
                                <Paper
                                    elevation={6}
                                    sx={{
                                        p: 4,
                                        borderRadius: 4,
                                        background: 'rgba(255,255,255,0.9)',
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <Box mb={4}>
                                        <Typography variant="h4" gutterBottom fontWeight="600" color="#1A365D"
                                                    align="center">
                                            Our Mission
                                        </Typography>
                                        <Typography variant="body1" sx={{fontSize: '1.1rem', lineHeight: 1.6}}>
                                            At Pivotal Behavioral Services, our mission is to empower individuals and
                                            families through high-quality, compassionate care. We strive to provide
                                            quality ABA therapy tailored to each individual's unique needs and
                                            goals. We support families by offering comprehensive resources, education, and
                                            guidance throughout their journey. Pivotal delivers evidence-based treatment that
                                            promotes meaningful and lasting positive change in our clients' lives,
                                            foster a collaborative environment that encourages continuous learning and
                                            professional growth among our team members. We advocate for increased
                                            awareness and acceptance of neurodiversity in our communities.
                                        </Typography>
                                        
                                    </Box>
                                    <Box>
                                        <Typography variant="h4" gutterBottom fontWeight="600" color="#1A365D"
                                                    align="center">
                                            Our Vision
                                        </Typography>
                                        <Typography variant="body1" sx={{fontSize: '1.1rem', lineHeight: 1.6}}>
                                            We are committed to serving a diverse community by providing
                                            person-centered, evidence-based treatment that fosters growth and success.
                                            Our vision is to create a world where every individual has the opportunity
                                            to reach their full potential, regardless of their challenges or background.
                                        </Typography>
                                    </Box>
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Core Values Section */}
            <Box sx={{py: 10, bgcolor: '#f8f9fa', width: '100%', overflow: 'hidden'}}>
                <Container maxWidth="lg">
                    <motion.div
                        initial={{opacity: 0, y: 30}}
                        whileInView={{opacity: 1, y: 0}}
                        viewport={{once: true}}
                        transition={{duration: 0.6}}
                    >
                        <Typography
                            variant="h3"
                            component="h2"
                            align="center"
                            gutterBottom
                            sx={{
                                fontWeight: 700,
                                mb: 7,
                                position: 'relative',
                                '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -16,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 100,
                                    height: 4,
                                    borderRadius: '2px',
                                    background: 'linear-gradient(90deg, #C7AE6A 0%, #1A365D 100%)',
                                }
                            }}
                        >
                            Core Values
                        </Typography>
                    </motion.div>

                    <Box sx={{
                        width: '100%',
                        mx: 'auto',
                        mb: 6,
                        display: 'flex',
                        flexDirection: {xs: 'column', sm: 'row'},
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'stretch',
                        gap: 3
                    }}>
                        {[
                            {
                                title: "Family-Oriented",
                                icon: <FamilyRestroomIcon sx={{fontSize: 60, color: '#0182ad'}}/>,
                                description: "We recognize the importance of family involvement in the therapeutic process."
                            },
                            {
                                title: "Excellence",
                                icon: <StarIcon sx={{fontSize: 60, color: '#C7AE6A'}}/>,
                                description: "We strive to create positive, lasting change in the lives of those we serve."
                            },
                            {
                                title: "Teamwork",
                                icon: <GroupsIcon sx={{fontSize: 60, color: '#000080'}}/>,
                                description: "We collaborate with families, educators, and other professionals for optimal outcomes."
                            },
                            {
                                title: "Meaningful Impact",
                                icon: <EmojiObjectsIcon sx={{fontSize: 60, color: '#FFDB58'}}/>,
                                description: "We celebrate every milestone and work toward meaningful, lasting progress."
                            },
                            {
                                title: "Innovation",
                                icon: <LightbulbIcon sx={{fontSize: 60, color: '#FF6B6B'}}/>,
                                description: "We continuously seek new and improved ways to deliver effective care and support."
                            },
                            {
                                title: "Integrity",
                                icon: <VerifiedUserIcon sx={{fontSize: 60, color: '#800020'}}/>,
                                description: "We uphold the highest ethical standards in all of our interactions and practices."
                            }
                        ].map((value, index) => (
                            <Box key={index} sx={{
                                flexBasis: {xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(33.333% - 16px)'},
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <motion.div
                                    initial={{opacity: 0, y: 30}}
                                    whileInView={{opacity: 1, y: 0}}
                                    viewport={{once: true}}
                                    transition={{delay: index * 0.2, duration: 0.5}}
                                >
                                    <Card sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        p: 3,
                                        boxShadow: 3,
                                        '&:hover': {
                                            boxShadow: 6,
                                            transform: 'translateY(-5px)',
                                            transition: 'all 0.3s ease-in-out'
                                        }
                                    }}>
                                        <Box sx={{
                                            mb: 2,
                                            p: 2,
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(26,54,93,0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            {value.icon}
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" component="h3" gutterBottom fontWeight="bold"
                                                        color="#1A365D">
                                                {value.title}
                                            </Typography>
                                            <Typography variant="body2">
                                                {value.description}
                                            </Typography>
                                        </Box>
                                    </Card>
                                </motion.div>
                            </Box>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* Testimonials Section */}
            <Box sx={{py: 8, bgcolor: '#f8f8f8'}}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        component="h2"
                        align="center"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            mb: 6,
                            position: 'relative',
                            '&:after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 80,
                                height: 3,
                                bgcolor: '#C7AE6A', // Gold underline
                            }
                        }}
                    >
                        Success Stories
                    </Typography>

                    <Grid container spacing={4}>
                        {[
                            {
                                quote: "The care and support from Pivotal Behavioral Services has been transformative. The therapists are compassionate, skilled, and truly dedicated to helping their clients succeed.",
                                name: "Parent of 4-year-old client",
                                role: "In-Home ABA Therapy"
                            },
                            {
                                quote: "Our family has seen incredible progress since starting services with Pivotal. The therapists work wonderfully with our child and have given us valuable tools to support their development at home.",
                                name: "Family of 16-year-old client",
                                role: "Parent Engagement Model"
                            },
                            {
                                quote: "As an educator, I've witnessed remarkable improvements in my students who receive support from Pivotal. Their collaborative approach ensures consistent strategies across home and school environments.",
                                name: "Elementary School Teacher",
                                role: "In-School ABA Support"
                            }
                        ].map((item, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <motion.div
                                    initial={{opacity: 0, y: 30}}
                                    whileInView={{opacity: 1, y: 0}}
                                    viewport={{once: true}}
                                    transition={{delay: index * 0.2, duration: 0.5}}
                                >
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            position: 'relative',
                                            p: 3,
                                            boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <CardContent sx={{flexGrow: 1, pt: 4}}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    mb: 2,
                                                    fontStyle: 'italic',
                                                    fontSize: '1.05rem',
                                                    lineHeight: 1.6
                                                }}
                                            >
                                                "{item.quote}"
                                            </Typography>
                                            <Box sx={{mt: 2, display: 'flex', flexDirection: 'column'}}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: '#1A365D'
                                                    }}
                                                >
                                                    {item.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#00a79c'
                                                    }}
                                                >
                                                    {item.role}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Call to Action */}
            <Box sx={{
                    py: 10,
                    textAlign: 'center',
                    bgcolor: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${heroExcitedChildren})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'brightness(0.15)',
                        zIndex: 0,
                    }
                }}>
                <Container maxWidth="md" sx={{position: 'relative', zIndex: 1}}>
                    <motion.div
                        initial={{opacity: 0, y: 30}}
                        whileInView={{opacity: 1, y: 0}}
                        viewport={{once: true}}
                        transition={{duration: 0.7}}
                    >
                        <Typography
                            variant="h3"
                            component="h2"
                            gutterBottom
                            sx={{
                                fontWeight: 700,
                                color: 'white',
                                textShadow: '1px 1px 3px rgba(0,0,0,0.3)'
                            }}
                        >
                            Ready to Take the First Step?
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 5,
                                color: 'rgba(255,255,255,0.9)',
                                maxWidth: '700px',
                                mx: 'auto'
                            }}
                        >
                            Contact us today to schedule a free consultation.
                            We're here to answer your questions and help you begin your journey.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            component={RouterLink}
                            to="/contact"
                            sx={{
                                bgcolor: '#C09C2C', // Gold button
                                color: 'white',
                                px: 5,
                                py: 1.8,
                                borderRadius: '30px',
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                boxShadow: '0 4px 20px rgba(212, 175, 55, 0.4)',
                                '&:hover': {
                                    bgcolor: '#C7AE6A', // Slightly darker gold on hover
                                    boxShadow: '0 6px 25px rgba(212, 175, 55, 0.5)',
                                }
                            }}
                        >
                            GET STARTED TODAY
                        </Button>
                    </motion.div>
                </Container>
            </Box>

            {/* Add the ConsultationPopup here */}
            <ConsultationPopup/>
            
            {/* Scroll to top button */}
            <ScrollTop {...props}>
                <Fab
                    color="primary"
                    size="small"
                    aria-label="scroll back to top"
                    sx={{
                        backgroundColor: '#61B3E5',
                        '&:hover': {
                            backgroundColor: '#4FA3D5',
                        },
                        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.2)',
                    }}
                >
                    <KeyboardArrowUpIcon />
                </Fab>
            </ScrollTop>
        </>
    );
};

export default Home;